import { styled } from '@mui/material';

import { getBackgroundColorForStatus, getColorForStatus } from '@/shared/lib/statusColors';

export const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  alignItems: 'flex-start',
  borderRadius: '0.5rem',
  border: `1px solid ${theme.palette.grey[200]}`,
  marginBottom: '1rem',
}));

export const StatusHeader = styled('div')(() => ({
  width: '100%',
  padding: '0.5rem',
  paddingBottom: 0,
  gap: '0.5rem',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}));

export const CommentHeader = styled('div')(() => ({
  width: '100%',
  padding: '0.5rem',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

export const UserName = styled('p')(({ theme }) => ({
  margin: 0,
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '150%',
  color: theme.palette.grey[900],
}));

export const HeaderSection = styled('div')`
  gap: 0.25rem;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const ContentSection = styled('div')(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.palette.grey[50],
  padding: '0.5rem',
  border: `1px solid ${theme.palette.grey[200]}`,
  borderLeftWidth: 0,
  borderRightWidth: 0,
}));

export const BodyText = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.grey[900],
  fontSize: '0.875rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '150%',
}));

export const ContentFooter = styled('div')`
  display: flex;
  flex-direction: row;
  width: 100%;
`;

export const FooterButton = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isLeft' && prop !== 'isRight',
})<{ isLeft?: boolean | undefined; isRight?: boolean | undefined }>(
  ({ theme, isLeft, isRight }) => ({
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0.75rem',
    cursor: 'pointer',
    gap: '0.25rem',
    borderLeft: isRight ? `0.5px solid ${theme.palette.grey[200]}` : 'none',
    borderRight: isLeft ? `0.5px solid ${theme.palette.grey[200]}` : 'none',

    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  }),
);

export const FooterButtonText = styled('p')(({ theme }) => ({
  color: theme.palette.grey[900],
  margin: 0,
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
}));

export const TimeAgo = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.grey[800],
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
}));

export const RepliesList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
  padding: '0.5rem',
  width: '100%',
  borderTop: `1px solid ${theme.palette.grey[200]}`,
}));

export const StatusLabel = styled('p')(({ theme }) => ({
  margin: 0,
  color: theme.palette.grey[700],
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '160%',
}));

export const PriorityValue = styled('div', {
  shouldForwardProp: (prop) => prop !== 'priority',
})<{ priority: 'LOW' | 'MEDIUM' | 'HIGH' }>(({ theme, priority }) => ({
  borderRadius: '0.125rem',
  padding: '0.25rem',
  color:
    priority === 'HIGH'
      ? theme.palette.red[700]
      : priority === 'MEDIUM'
        ? theme.palette.orange[700]
        : theme.palette.purple[700],
  backgroundColor:
    priority === 'HIGH'
      ? theme.palette.red[100]
      : priority === 'MEDIUM'
        ? theme.palette.orange[100]
        : theme.palette.purple[100],
  fontSize: '0.625rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: 'normal',
  textTransform: 'uppercase',
}));

export const StatusValue = styled('div', {
  shouldForwardProp: (prop) => prop !== 'status',
})<{
  status:
    | 'NOT_STARTED'
    | 'DONE'
    | 'AVAILABLE'
    | 'ACTIVE'
    | 'COMPLETED'
    | 'PARTIALLY_AVAILABLE'
    | 'BOOKED'
    | 'APPROVED'
    | 'CONFIRMED'
    | 'DRAFT'
    | 'PENDING'
    | 'CANCELLED'
    | 'PRINT_PROOF_REQUIRED'
    | 'BLOCKED'
    | 'IN_PROGRESS'
    | 'SHIPPED'
    | 'IN_REVIEW'
    | 'REJECTED';
}>(({ theme, status }) => ({
  borderRadius: '0.4375rem',
  padding: '0 0.44rem',
  color: getColorForStatus(status, theme),
  backgroundColor: getBackgroundColorForStatus(status, theme),
  fontSize: '0.75rem',
  fontStyle: 'normal',
  fontWeight: 500,
  lineHeight: '1.25rem',
  textTransform: 'uppercase',
}));

export const MenuItemNotStarted = styled('div')(({ theme }) => ({
  padding: '0.12rem 0.25rem',
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.grey[600],
  borderRadius: '0.25rem',
}));

export const MenuItemInProgress = styled('div')(({ theme }) => ({
  padding: '0.12rem 0.25rem',
  // @ts-ignore
  backgroundColor: theme.palette.blue[100],
  // @ts-ignore
  color: theme.palette.blue[600],
  borderRadius: '0.25rem',
}));

export const MenuItemDone = styled('div')(({ theme }) => ({
  padding: '0.12rem 0.25rem',
  backgroundColor: theme.palette.green[100],
  color: theme.palette.green[600],
  borderRadius: '0.25rem',
}));

export const MenuItemRejected = styled('div')(({ theme }) => ({
  padding: '0.12rem 0.25rem',
  backgroundColor: theme.palette.red[100],
  color: theme.palette.red[600],
  borderRadius: '0.25rem',
}));
